// Server-driven rules for the host lobby "unsupported browser" warning
// (AdminPortalV2 > Browser Support, served by GET api/public/browser-support).
// Mirrors services/promoConfig.js: module singleton, localStorage TTL cache,
// in-flight de-dupe, and a graceful default so the app works if the API is down.

const CACHE_KEY = 'browserSupportConfig';
const DEFAULT_CACHE_DURATION_MS = 10 * 60 * 1000; // 10 min fallback
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const consoleRule = (id, vendor) => ({
    id,
    title: null,
    message: null,
    conditions: [
        { field: 'device.vendor', operator: 'equals', value: vendor },
        { field: 'device.type', operator: 'equals', value: 'console' },
    ],
});

class BrowserSupportConfigManager {
    constructor() {
        this.config = null;
        this.loadPromise = null;
        this.cacheDurationMs = DEFAULT_CACHE_DURATION_MS;
    }

    // Used until an admin saves a config (hasConfig: false), or when the API can't be reached.
    // Keep in sync with BrowserSupportConfig.CreateDefault() in AdminPortalV2.
    getDefaultConfig() {
        return {
            enabled: true,
            revision: 0,
            title: 'Heads up!',
            message: "We haven't tested Game City on this browser yet, so some things might look or behave a little differently. You're welcome to carry on, but for the best experience try hosting on a laptop, tablet or smart TV browser.",
            rules: [
                consoleRule('default-playstation', 'Sony'),
                consoleRule('default-xbox', 'Microsoft'),
                consoleRule('default-nintendo', 'Nintendo'),
            ],
        };
    }

    // Map the API DTO into the stable config shape the lobby reads.
    normalize(data) {
        const def = this.getDefaultConfig();
        if (!data || data.hasConfig === false) {
            return def;
        }
        // Saved config is authoritative: enabled:false (switched off, or bad JSON server-side)
        // means no warning at all — never fall back to the default rules.
        return {
            enabled: data.enabled === true,
            revision: data.revision ?? 0,
            title: data.title || def.title,
            message: data.message || def.message,
            rules: Array.isArray(data.rules)
                ? data.rules.filter(r => r && r.id && Array.isArray(r.conditions) && r.conditions.length > 0)
                : [],
        };
    }

    getCachedConfig() {
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (!cached) return null;
            const { data, timestamp, ttl } = JSON.parse(cached);
            const cacheDuration = ttl || DEFAULT_CACHE_DURATION_MS;
            if (Date.now() - timestamp < cacheDuration) {
                this.cacheDurationMs = cacheDuration;
                return data;
            }
            localStorage.removeItem(CACHE_KEY);
            return null;
        } catch (error) {
            console.error('Error reading cached browser support config:', error);
            return null;
        }
    }

    setCachedConfig(data) {
        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
                data,
                timestamp: Date.now(),
                ttl: this.cacheDurationMs,
            }));
        } catch (error) {
            console.error('Error caching browser support config:', error);
        }
    }

    async fetchFromAPI() {
        const response = await fetch(`${API_BASE_URL}api/public/browser-support`);
        if (!response.ok) {
            throw new Error('Failed to fetch browser support config');
        }
        const data = await response.json();
        if (data.cacheTtlSeconds) {
            this.cacheDurationMs = data.cacheTtlSeconds * 1000;
        }
        return this.normalize(data);
    }

    async load() {
        if (this.loadPromise) {
            return this.loadPromise;
        }

        const cached = this.getCachedConfig();
        if (cached) {
            this.config = cached;
            return cached;
        }

        this.loadPromise = this.fetchFromAPI()
            .then(data => {
                this.config = data;
                this.setCachedConfig(data);
                this.loadPromise = null;
                return data;
            })
            .catch(error => {
                this.loadPromise = null;
                console.error('Failed to load browser support config, using defaults');
                this.config = this.getDefaultConfig();
                return this.config;
            });

        return this.loadPromise;
    }

    getConfig() {
        return this.config || this.getDefaultConfig();
    }
}

// Export a singleton instance
export const browserSupportConfig = new BrowserSupportConfigManager();
